import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuthStore } from "@/features/auth/store/auth-store";

/** Session flag: set once we've landed a first-login owner on /onboarding, so we
 * don't re-bounce them every time they navigate the shell (or reload the tab). */
const LANDED_KEY = "nsi.onboardingLanded";

/**
 * First-login gate for firm administrators. Wraps the authenticated app shell
 * (AppLayout) in routes/index.tsx — and `/onboarding` now lives *inside* that
 * shell (a child of AppLayout), so the onboarding hub renders with the normal
 * sidebar/header chrome.
 *
 * A firm's founding administrator is sent to `/onboarding` on first login, but
 * only *once per tab session*: after that they can roam the shell freely (the
 * sidebar keeps a "Getting started" pill to return). The trigger keys off a
 * strict `onboarding_completed === false`; an absent/true flag (e.g. a
 * pre-existing persisted session) falls through, so a session is never trapped.
 *
 * Loop-safety: we never redirect when already on `/onboarding`, and the
 * `LANDED_KEY` flag guarantees at most one auto-redirect per tab.
 */
export function OnboardingGate() {
  const user = useAuthStore((s) => s.user);
  const { pathname } = useLocation();
  const onOnboarding = pathname === "/onboarding";

  const needsOnboarding =
    user?.account_type === "firm" &&
    user.role === "country_director" &&
    user.firm?.onboarding_completed === false;

  const alreadyLanded =
    typeof sessionStorage !== "undefined" && sessionStorage.getItem(LANDED_KEY) === "1";

  if (needsOnboarding && !alreadyLanded) {
    // Mark as landed and send them to the hub the first time; subsequent
    // navigations (and this render, once they're on /onboarding) fall through.
    try {
      sessionStorage.setItem(LANDED_KEY, "1");
    } catch {
      /* storage unavailable (private mode) — degrade to a single redirect */
    }
    if (!onOnboarding) return <Navigate to="/onboarding" replace />;
  }

  return <Outlet />;
}
